/**
 * Centralized HTTP Error Handler
 *
 * This module provides unified handling for HTTP errors (4xx, 5xx) by displaying
 * them as toast notifications at the top of the screen. Form validation errors
 * within components are handled separately and are not affected by this handler.
 */

import { toast } from 'sonner';
import { getErrorKey } from './errors';

// HTTP status codes that should trigger toast notifications
const TOAST_ERROR_STATUS_CODES = [400, 401, 403, 404, 422, 429, 500, 502, 503, 504];

// Error types that should NOT trigger toast notifications (handled by components)
const LOCAL_ERROR_TYPES = ['validation', 'form', 'duplicate'];

export interface HttpErrorOptions {
  /** Skip toast notification for this error */
  silent?: boolean;
  /** Custom error message to display instead of the default */
  customMessage?: string;
  /** Additional context for error logging */
  context?: string;
  /** Translation function for internationalization */
  t?: (key: string, defaultValue?: string) => string;
}

/**
 * Extract error message from various error response formats
 */
function extractErrorMessage(error: any): string | null {
  // Handle Axios error with response data
  if (error?.response?.data) {
    const data = error.response.data;

    // Try common error message fields
    return (
      data?.error_message ||
      data?.error?.message ||
      data?.message ||
      data?.detail ||
      data?.error ||
      null
    );
  }

  // Handle plain error with message
  if (error?.message) {
    return error.message;
  }

  return null;
}

/**
 * Get localized error message based on error key
 */
function getLocalizedErrorMessage(
  error: any,
  t?: (key: string, defaultValue?: string) => string
): string {
  const errorKey = getErrorKey(error, 'api');

  if (t) {
    const translated = t(errorKey);
    // Return translation if it's not the same as the key (meaning translation exists)
    if (translated !== errorKey) {
      return translated;
    }
  }

  // Fallback to extracted message or generic message
  const extractedMessage = extractErrorMessage(error);
  return (
    extractedMessage ||
    t?.('errors.unknown', 'An unknown error occurred') ||
    'An unknown error occurred'
  );
}

/**
 * Check if an error should be handled by the global HTTP error handler
 * Returns false for local validation/form errors that should be handled by components
 */
function shouldHandleGlobally(error: any, options: HttpErrorOptions = {}): boolean {
  // Skip if explicitly marked as silent
  if (options.silent) {
    return false;
  }

  // Skip if error has custom message (handled by caller)
  if (options.customMessage) {
    return false;
  }

  // Check if error is marked as a local error type
  const errorType = error?.response?.data?.error_type || error?.type;
  if (LOCAL_ERROR_TYPES.includes(errorType)) {
    return false;
  }

  // Handle HTTP errors with specific status codes
  const status = error?.response?.status;
  if (status && TOAST_ERROR_STATUS_CODES.includes(status)) {
    return true;
  }

  // Handle network errors without response (connection issues)
  if (!error?.response && (error?.code === 'ERR_NETWORK' || error?.code === 'ECONNABORTED')) {
    return true;
  }

  return false;
}

/**
 * Handle HTTP error by displaying toast notification
 *
 * This function should be called from API interceptors to handle
 * HTTP errors (4xx, 5xx) consistently across the application.
 *
 * @param error - The error object from axios
 * @param options - Optional configuration for error handling
 * @returns true if error was handled, false if it should be handled elsewhere
 *
 * @example
 * ```ts
 * api.interceptors.response.use(
 *   response => response,
 *   error => {
 *     handleHttpError(error, { t });
 *     return Promise.reject(error);
 *   }
 * );
 * ```
 */
export function handleHttpError(error: any, options: HttpErrorOptions = {}): boolean {
  if (!shouldHandleGlobally(error, options)) {
    return false;
  }

  const { t, context } = options;

  // Log error for debugging
  if (context) {
    console.warn(`[HttpErrorHandler] ${context}:`, error);
  } else {
    console.warn('[HttpErrorHandler] HTTP Error:', error);
  }

  // Get error message
  const errorMessage = getLocalizedErrorMessage(error, t);

  // Display toast notification
  toast.error(errorMessage, {
    duration: 5000,
    position: 'top-right',
  });

  return true;
}

/**
 * Create a typed error handler for specific API calls
 *
 * @example
 * ```ts
 * const errorHandler = createHttpErrorHandler(t, 'UserService');
 * try {
 *   await userService.create(data);
 * } catch (error) {
 *   errorHandler(error);
 *   throw error;
 * }
 * ```
 */
export function createHttpErrorHandler(
  t?: (key: string, defaultValue?: string) => string,
  context?: string
): (error: any) => void {
  return (error: any) => {
    handleHttpError(error, { t, context });
  };
}

export default {
  handleHttpError,
  createHttpErrorHandler,
};
